import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Chip, Alert, IconButton,
  TablePagination, ToggleButtonGroup, ToggleButton,
  Divider, Button, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, CircularProgress,
  TextField, InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MarkEmailRead as InboxIcon,
  OpenInNew as OpenInNewIcon,
  AutoFixHigh as ReclassifyIcon,
  Forum as ThreadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PersonAdd as AssignIcon,
  Reply as ReplyIcon,
  AutoFixHigh as ImproveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  listInbox, getInboxCounts, refreshImapInbox,
  reclassifyInboxItem, getInboxThread, getAiProviders,
  getImapPollerStatus, pauseImapPoller, resumeImapPoller,
  getLeads, getLeadContactsLight, assignInboundItem,
  improveEmail, sendEmail,
} from '../services/api';

const LS_AI_ENABLED = 'inbox.ai.enabled';
const LS_AI_PROVIDER = 'inbox.ai.provider';
const LS_READ_IDS = 'inbox.read.ids';
const DEFAULT_PROVIDER = 'openai';

function loadReadIds() {
  try {
    const raw = localStorage.getItem(LS_READ_IDS);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}

function saveReadIds(set) {
  try {
    const arr = Array.from(set).slice(-2000);
    localStorage.setItem(LS_READ_IDS, JSON.stringify(arr));
  } catch { }
}

const INTENT_FILTERS = ['All', 'Interested', 'NeedMoreInfo', 'NotNow', 'NotRelevant', 'Unsubscribe', 'Unknown'];
const INTENT_COLOR = {
  Interested: 'success',
  NeedMoreInfo: 'info',
  NotNow: 'warning',
  NotRelevant: 'error',
  Unsubscribe: 'error',
  Unknown: 'default',
};

const SANITIZE_OPTS = {
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'meta', 'link'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'formaction', 'srcdoc'],
};

function ListCard({ item, selected, unread, onClick }) {
  const date = item.receivedDate ? new Date(item.receivedDate) : null;
  const dateStr = date ? date.toLocaleDateString() : '—';
  const timeStr = date ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        p: 1.25, pl: unread ? 1.75 : 1.25, borderRadius: 1.5, cursor: 'pointer',
        border: '1px solid',
        borderColor: selected ? '#6366f1' : '#e2e8f0',
        bgcolor: selected ? '#eef2ff' : (unread ? '#fafbff' : '#fff'),
        mb: 0.75,
        transition: 'background-color .12s, border-color .12s',
        '&:hover': { bgcolor: selected ? '#eef2ff' : '#f8fafc' },
        '&::before': unread ? {
          content: '""', position: 'absolute', left: 0, top: 6, bottom: 6,
          width: 3, borderRadius: 2, bgcolor: '#6366f1',
        } : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
        <Typography variant="caption" sx={{
          color: unread ? '#0f172a' : '#64748b',
          fontSize: '0.7rem',
          fontWeight: unread ? 700 : 400,
        }}>
          {dateStr} {timeStr}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label={item.aiIntent || 'Unknown'}
          color={INTENT_COLOR[item.aiIntent || 'Unknown'] || 'default'}
          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
        />
      </Stack>
      <Typography variant="body2" sx={{
        fontWeight: unread ? 800 : 600,
        lineHeight: 1.25, color: '#0f172a', mb: 0.25,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {item.contactName || item.contactEmail || '—'}
      </Typography>
      <Typography variant="body2" sx={{
        fontSize: '0.82rem',
        color: unread ? '#0f172a' : '#334155',
        fontWeight: unread ? 700 : 400,
        mb: 0.25,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {item.subject || '(no subject)'}
      </Typography>
      <Typography variant="caption" sx={{ color: item.isUnmatched ? '#b45309' : '#6366f1', fontWeight: unread ? 700 : 600 }}>
        {item.isUnmatched ? 'UNASSIGNED' : `#${item.leadId} ${item.companyName}`}
      </Typography>
    </Box>
  );
}

export default function InboxPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [intent, setIntent] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshInfo, setRefreshInfo] = useState('');
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [readIds, setReadIds] = useState(() => loadReadIds());

  const markRead = useCallback((id) => {
    if (id == null) return;
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      saveReadIds(next);
      return next;
    });
  }, []);

  const selectItem = useCallback((id) => {
    setSelectedId(id);
    markRead(id);
  }, [markRead]);

  const [aiEnabled, setAiEnabled] = useState(() => {
    const stored = localStorage.getItem(LS_AI_ENABLED);
    return stored == null ? true : stored === 'true';
  });
  const [aiProvider, setAiProvider] = useState(() => localStorage.getItem(LS_AI_PROVIDER) || DEFAULT_PROVIDER);
  const [providers, setProviders] = useState([]);

  const [reclassifyingId, setReclassifyingId] = useState(null);

  const [thread, setThread] = useState({ open: false, loading: false, leadId: null, messages: [], error: '' });

  const [pollerStatus, setPollerStatus] = useState(null);
  const [pollerWorking, setPollerWorking] = useState(false);

  const [assignDialog, setAssignDialog] = useState({
    open: false, item: null,
    leadQuery: '', leadResults: [], leadLoading: false,
    selectedLead: null, contacts: [], contactsLoading: false,
    selectedContactId: null,
    createNew: false, newEmail: '', newFirst: '', newLast: '',
    saving: false, error: '',
  });

  const [replyDialog, setReplyDialog] = useState({
    open: false, item: null,
    to: '', subject: '', body: '',
    customInstructions: '',
    improving: false, sending: false, error: '', success: '',
  });

  useEffect(() => { localStorage.setItem(LS_AI_ENABLED, String(aiEnabled)); }, [aiEnabled]);
  useEffect(() => { localStorage.setItem(LS_AI_PROVIDER, aiProvider); }, [aiProvider]);

  useEffect(() => {
    getAiProviders()
      .then(({ data }) => {
        const list = (data || []).filter((p) => p.isAvailable !== false);
        setProviders(list);
        if (list.length && !list.some((p) => p.name === aiProvider)) {
          const fallback = list.find((p) => p.isDefault)?.name
            || list.find((p) => p.name === DEFAULT_PROVIDER)?.name
            || list[0].name;
          setAiProvider(fallback);
        }
      })
      .catch(() => { });
  }, []);// eslint-disable-line

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * rowsPerPage, take: rowsPerPage };
      if (intent && intent !== 'All') params.intent = intent;
      if (search) params.q = search;
      const { data } = await listInbox(params);
      setItems(data || []);
      setSelectedId((prev) => {
        if (prev && (data || []).some((x) => x.id === prev)) return prev;
        return (data || [])[0]?.id ?? null;
      });
    } catch (e) { setError(e?.response?.data?.message || 'Failed to load inbox'); }
    finally { setLoading(false); }
  }, [intent, page, rowsPerPage, search]);

  const loadCounts = useCallback(async () => {
    try { const { data } = await getInboxCounts(); setCounts(data); } catch { }
  }, []);

  const openAssign = useCallback((item) => {
    setAssignDialog({
      open: true, item,
      leadQuery: '', leadResults: [], leadLoading: false,
      selectedLead: null, contacts: [], contactsLoading: false,
      selectedContactId: null,
      createNew: false, newEmail: item?.fromEmailRaw || '', newFirst: '', newLast: '',
      saving: false, error: '',
    });
  }, []);

  const closeAssign = useCallback(() => {
    setAssignDialog((d) => ({ ...d, open: false }));
  }, []);

  useEffect(() => {
    if (!assignDialog.open) return;
    const q = assignDialog.leadQuery.trim();
    if (q.length < 2) {
      setAssignDialog((d) => ({ ...d, leadResults: [], leadLoading: false }));
      return;
    }
    setAssignDialog((d) => ({ ...d, leadLoading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await getLeads({ search: q, take: 15 });
        const list = data?.items ?? data ?? [];
        setAssignDialog((d) => ({ ...d, leadResults: list, leadLoading: false }));
      } catch {
        setAssignDialog((d) => ({ ...d, leadResults: [], leadLoading: false }));
      }
    }, 300);
    return () => clearTimeout(t);
  }, [assignDialog.open, assignDialog.leadQuery]);

  const pickLead = useCallback(async (lead) => {
    setAssignDialog((d) => ({
      ...d, selectedLead: lead, contactsLoading: true, contacts: [], selectedContactId: null,
    }));
    try {
      const { data } = await getLeadContactsLight(lead.id);
      setAssignDialog((d) => ({ ...d, contacts: data || [], contactsLoading: false }));
    } catch {
      setAssignDialog((d) => ({ ...d, contacts: [], contactsLoading: false }));
    }
  }, []);

  const openReply = useCallback((item) => {
    if (!item) return;
    const to = item.contactEmail || item.fromEmailRaw || '';
    const baseSubject = item.subject || '';
    const subj = /^re:\s/i.test(baseSubject) ? baseSubject : `Re: ${baseSubject}`;
    setReplyDialog({
      open: true, item, to, subject: subj, body: '',
      customInstructions: '', improving: false, sending: false, error: '', success: '',
    });
  }, []);

  const closeReply = useCallback(() => {
    setReplyDialog((d) => ({ ...d, open: false }));
  }, []);

  const handleImproveReply = useCallback(async () => {
    setReplyDialog((d) => ({ ...d, improving: true, error: '' }));
    try {
      const d = replyDialog;
      const { data } = await improveEmail({
        leadId: d.item.leadId,
        contactId: d.item.contactId || null,
        subject: d.subject,
        body: d.body,
        currentSubject: d.subject,
        currentBody: d.body,
        action: 'improve',
        aiProviderName: aiProvider || undefined,
        customInstructions: d.customInstructions?.trim() || null,
      });
      setReplyDialog((x) => ({
        ...x,
        subject: data?.improvedSubject || x.subject,
        body: data?.improvedBody || x.body,
        improving: false,
      }));
    } catch (e) {
      setReplyDialog((x) => ({ ...x, improving: false, error: e?.response?.data?.message || 'AI improve failed' }));
    }
  }, [replyDialog, aiProvider]);

  const handleSendReply = useCallback(async () => {
    setReplyDialog((d) => ({ ...d, sending: true, error: '' }));
    try {
      const d = replyDialog;
      const { data } = await sendEmail({
        leadId: d.item.leadId,
        contactId: d.item.contactId || null,
        subject: d.subject,
        body: d.body,
        recipientEmail: d.to,
      });
      if (data?.success === false) throw new Error(data?.errorMessage || 'Send failed');
      setReplyDialog((x) => ({ ...x, sending: false, success: 'Reply sent.', open: false }));
      setRefreshInfo('Reply sent.');
      loadItems();
    } catch (e) {
      setReplyDialog((x) => ({ ...x, sending: false, error: e?.response?.data?.errorMessage || e?.response?.data?.message || e.message || 'Send failed' }));
    }
  }, [replyDialog, loadItems]);

  const submitAssign = useCallback(async () => {
    setAssignDialog((x) => {
      if (!x.item || !x.selectedLead) return x;
      return { ...x, saving: true, error: '' };
    });
    const d = assignDialog;
    if (!d.item || !d.selectedLead) return;
    try {
      const body = {
        leadId: d.selectedLead.id,
        contactId: d.createNew ? null : d.selectedContactId,
        createContact: d.createNew,
        newContactEmail: d.createNew ? d.newEmail : null,
        newContactFirstName: d.createNew ? d.newFirst : null,
        newContactLastName: d.createNew ? d.newLast : null,
      };
      await assignInboundItem(d.item.id, body);
      setAssignDialog((x) => ({ ...x, open: false, saving: false }));
      loadItems();
      loadCounts();
    } catch (e) {
      setAssignDialog((x) => ({ ...x, saving: false, error: e?.response?.data?.error || 'Assign failed' }));
    }
  }, [assignDialog, loadItems, loadCounts]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshInfo('');
    try {
      const params = { aiEnabled };
      if (aiEnabled && aiProvider) params.provider = aiProvider;
      const { data } = await refreshImapInbox(params);
      if (data?.error) {
        setError(`IMAP fetch error: ${data.error}`);
      } else {
        const aiTag = aiEnabled ? ` (AI: ${aiProvider})` : ' (AI off)';
        setRefreshInfo(`Fetched ${data?.fetched ?? 0} new message(s), ${data?.matched ?? 0} matched to leads.${aiTag}`);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to refresh from IMAP');
    } finally {
      setRefreshing(false);
      await Promise.all([loadItems(), loadCounts()]);
    }
  }, [loadItems, loadCounts, aiEnabled, aiProvider]);

  const handleReclassify = useCallback(async (item) => {
    setReclassifyingId(item.id);
    setError('');
    try {
      const { data } = await reclassifyInboxItem(item.id, aiProvider || undefined);
      setItems((prev) => prev.map((row) => row.id === item.id
        ? { ...row, aiSummary: data.aiSummary, aiIntent: data.aiIntent, aiSuggestedNextStep: data.aiSuggestedNextStep }
        : row));
      loadCounts();
    } catch (e) {
      setError(e?.response?.data?.error || 'AI reclassify failed');
    } finally {
      setReclassifyingId(null);
    }
  }, [aiProvider, loadCounts]);

  const loadPollerStatus = useCallback(async () => {
    try { const { data } = await getImapPollerStatus(); setPollerStatus(data); }
    catch { }
  }, []);

  useEffect(() => {
    loadPollerStatus();
    const t = setInterval(loadPollerStatus, 30000);
    return () => clearInterval(t);
  }, [loadPollerStatus]);

  const togglePoller = useCallback(async () => {
    if (!pollerStatus) return;
    setPollerWorking(true);
    try {
      if (pollerStatus.paused) await resumeImapPoller();
      else await pauseImapPoller();
      await loadPollerStatus();
    } catch (e) {
      setError(e?.response?.data?.error || 'Poller toggle failed');
    } finally {
      setPollerWorking(false);
    }
  }, [pollerStatus, loadPollerStatus]);

  const handleOpenThread = useCallback(async (item) => {
    setThread({ open: true, loading: true, leadId: item.leadId, messages: [], error: '' });
    try {
      const { data } = await getInboxThread(item.id);
      setThread({ open: true, loading: false, leadId: data.leadId, messages: data.messages || [], error: '' });
    } catch (e) {
      setThread({ open: true, loading: false, leadId: item.leadId, messages: [], error: e?.response?.data?.error || 'Failed to load conversation' });
    }
  }, []);

  const selected = useMemo(() => items.find((x) => x.id === selectedId) || null, [items, selectedId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 96px)' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <InboxIcon color="primary" />
        <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.25rem' }}>Inbox</Typography>
        <Box sx={{ flex: 1 }} />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>AI provider</InputLabel>
          <Select
            label="AI provider"
            value={aiProvider}
            onChange={(e) => setAiProvider(e.target.value)}
            disabled={!aiEnabled}
          >
            {providers.length === 0 && (
              <MenuItem value={aiProvider}>{aiProvider}</MenuItem>
            )}
            {providers.map((p) => (
              <MenuItem key={p.name} value={p.name}>
                {p.displayName || p.name}{p.isDefault ? ' (default)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Switch checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} />}
          label={aiEnabled ? 'AI on' : 'AI off'}
          sx={{ mr: 0 }}
        />

        <Tooltip title={pollerStatus
          ? (pollerStatus.paused
              ? `Auto-fetch PAUSED${pollerStatus.pausedBy ? ` by ${pollerStatus.pausedBy}` : ''}. Click to resume.`
              : `Auto-fetch RUNNING. Last tick: ${pollerStatus.lastTickAt ? new Date(pollerStatus.lastTickAt).toLocaleString() : '—'} (fetched ${pollerStatus.lastTickFetched ?? 0}, matched ${pollerStatus.lastTickMatched ?? 0}). Click to pause.`)
          : 'Auto-fetch status unknown'}>
          <span>
            <Button
              size="small"
              variant={pollerStatus?.paused ? 'contained' : 'outlined'}
              color={pollerStatus?.paused ? 'warning' : 'success'}
              startIcon={pollerStatus?.paused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
              onClick={togglePoller}
              disabled={!pollerStatus || pollerWorking || !pollerStatus.enabled}
              sx={{ textTransform: 'none' }}
            >
              {!pollerStatus
                ? 'Auto-fetch …'
                : !pollerStatus.enabled
                  ? 'Auto-fetch off'
                  : pollerStatus.paused
                    ? 'Resume auto-fetch'
                    : 'Pause auto-fetch'}
            </Button>
          </span>
        </Tooltip>

        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ textTransform: 'none' }}
        >
          {refreshing ? 'Fetching…' : 'Fetch from Hostinger'}
        </Button>
        <IconButton size="small" onClick={() => { loadItems(); loadCounts(); loadPollerStatus(); }} title="Reload list"><RefreshIcon fontSize="small" /></IconButton>
      </Stack>

      {refreshInfo && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setRefreshInfo('')}>{refreshInfo}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {counts && (
        <Paper variant="outlined" sx={{ p: 1, mb: 1.5, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: '#64748b', mr: 1 }}>
              Total inbound: <strong>{counts.total}</strong>
            </Typography>
            {Object.entries(counts.byIntent || {}).map(([k, v]) => (
              <Chip key={k} size="small" label={`${k}: ${v}`} color={INTENT_COLOR[k] || 'default'} variant="outlined" />
            ))}
          </Stack>
        </Paper>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#475569' }}>Replies</Typography>
        <TextField
          size="small"
          placeholder="Search email, lead, subject…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: 1, maxWidth: 380 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" value={intent} exclusive onChange={(_, v) => v && (setIntent(v), setPage(0))}>
          {INTENT_FILTERS.map((s) => <ToggleButton key={s} value={s} sx={{ textTransform: 'none', px: 1.25 }}>{s}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1.5 }}>
        {/* Left list pane */}
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {loading && <Typography sx={{ color: '#94a3b8', p: 1 }}>Loading…</Typography>}
            {!loading && items.length === 0 && (
              <Typography sx={{ color: '#94a3b8', fontStyle: 'italic', p: 1 }}>No inbound replies in the current filter.</Typography>
            )}
            {!loading && items.map((it) => (
              <ListCard
                key={it.id}
                item={it}
                selected={it.id === selectedId}
                unread={!readIds.has(it.id)}
                onClick={() => selectItem(it.id)}
              />
            ))}
          </Box>
          <Divider />
          <TablePagination
            component="div"
            count={-1}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelDisplayedRows={({ from, to }) => `${from}–${to}`}
            sx={{ '& .MuiTablePagination-toolbar': { minHeight: 44, px: 1 } }}
          />
        </Paper>

        {/* Right detail pane */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          {/* Body card */}
          <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius: 2, minHeight: 420 }}>
            {selected ? (
              <>
                <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={selected.aiIntent || 'Unknown'}
                      color={INTENT_COLOR[selected.aiIntent || 'Unknown'] || 'default'}
                    />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {selected.receivedDate ? new Date(selected.receivedDate).toLocaleString() : '—'}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={`Re-run AI (${aiProvider})`}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleReclassify(selected)}
                          disabled={reclassifyingId === selected.id}
                        >
                          {reclassifyingId === selected.id
                            ? <CircularProgress size={16} />
                            : <ReclassifyIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Preview conversation">
                      <IconButton size="small" onClick={() => handleOpenThread(selected)}>
                        <ThreadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={selected.isUnmatched ? 'Assign before replying' : 'Reply'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openReply(selected)}
                          disabled={selected.isUnmatched || !(selected.contactEmail || selected.fromEmailRaw)}
                          color="primary">
                          <ReplyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={selected.isUnmatched ? 'Assign to lead' : 'Reassign to different lead'}>
                      <IconButton size="small" onClick={() => openAssign(selected)} color={selected.isUnmatched ? 'warning' : 'default'}>
                        <AssignIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open lead">
                      <span>
                        <IconButton size="small" component={RouterLink} to={`/leads/${selected.leadId}`} disabled={selected.isUnmatched}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                    {selected.subject || '(no subject)'}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.25 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      <strong>From:</strong>{' '}
                      {selected.contactName || (selected.isUnmatched ? '(unknown contact)' : '—')}
                      {(selected.contactEmail || selected.fromEmailRaw) ? (
                        <> &lt;<a href={`mailto:${selected.contactEmail || selected.fromEmailRaw}`} style={{ color: '#0284c7' }}>
                          {selected.contactEmail || selected.fromEmailRaw}
                        </a>&gt;</>
                      ) : ''}
                    </Typography>
                    {selected.isUnmatched ? (
                      <Chip size="small" color="warning" label="Unassigned — click Assign to link to a lead" />
                    ) : (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        <strong>Lead:</strong>{' '}
                        <RouterLink to={`/leads/${selected.leadId}`} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                          #{selected.leadId} {selected.companyName}
                        </RouterLink>
                      </Typography>
                    )}
                    {selected.repliedToInteractionId && (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        <strong>Replied to:</strong> {selected.repliedToSubject}
                        {selected.repliedToJobId && <> · Job #{selected.repliedToJobId}</>}
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
                  {selected.body
                    ? <Box
                        sx={{
                          fontSize: '0.9rem', color: '#0f172a',
                          '& img': { maxWidth: '100%' },
                          '& a': { wordBreak: 'break-all' },
                          '& blockquote': { borderLeft: '3px solid #cbd5e1', pl: 1, ml: 0, color: '#64748b' },
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.body, SANITIZE_OPTS) }}
                      />
                    : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#334155' }}>
                        {selected.bodyPreview || selected.aiSummary || '—'}
                      </Typography>}
                </Box>
              </>
            ) : (
              <Box sx={{ p: 4, color: '#94a3b8', textAlign: 'center' }}>
                <Typography variant="body2">Select a message to view it.</Typography>
              </Box>
            )}
          </Paper>

          {/* Bottom row: NextSteps + AI summary */}
          {selected && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ minHeight: 140 }}>
              <Paper variant="outlined" sx={{ flex: 1, p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Next steps
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: '#0284c7' }}>
                  {selected.aiSuggestedNextStep || '—'}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  AI summary
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: '#0f172a' }}>
                  {selected.aiSummary || '—'}
                </Typography>
              </Paper>
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={thread.open}
        onClose={() => setThread((t) => ({ ...t, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ThreadIcon fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>Conversation</Typography>
            {thread.leadId && (
              <Chip size="small" component={RouterLink} clickable to={`/leads/${thread.leadId}`} label={`Lead #${thread.leadId}`} sx={{ ml: 1 }} />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {thread.loading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2 }}>
              <CircularProgress size={18} /><Typography>Loading conversation…</Typography>
            </Stack>
          )}
          {!thread.loading && thread.error && <Alert severity="error">{thread.error}</Alert>}
          {!thread.loading && !thread.error && thread.messages.length === 0 && (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>No messages in this conversation.</Typography>
          )}
          {!thread.loading && thread.messages.map((m) => {
            const isInbound = m.direction === 'Inbound';
            return (
              <Box key={m.id} sx={{
                mb: 1.5, p: 1.5, borderRadius: 2,
                bgcolor: isInbound ? '#f1f5f9' : '#eef2ff',
                borderLeft: `3px solid ${isInbound ? '#0284c7' : '#6366f1'}`,
              }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Chip size="small" label={m.direction} color={isInbound ? 'info' : 'primary'} variant="outlined" />
                  {m.aiIntent && <Chip size="small" label={m.aiIntent} color={INTENT_COLOR[m.aiIntent] || 'default'} />}
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {m.contactName || ''} {m.contactEmail ? `<${m.contactEmail}>` : ''}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {new Date(m.receivedDate || m.interactionDate).toLocaleString()}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{m.subject || '(no subject)'}</Typography>
                <Box
                  sx={{
                    fontSize: '0.85rem', color: '#0f172a',
                    maxHeight: 280, overflow: 'auto',
                    '& img': { maxWidth: '100%' },
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body || '', { FORBID_TAGS: ['script', 'style', 'iframe'], FORBID_ATTR: ['onerror', 'onload', 'onclick'] }) }}
                />
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThread((t) => ({ ...t, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialog.open} onClose={closeAssign} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssignIcon fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>Assign inbound to lead</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {assignDialog.item && (
            <Box sx={{ mb: 2, p: 1.25, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#64748b' }}>From:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {assignDialog.item.fromEmailRaw || assignDialog.item.contactEmail || '(unknown)'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>Subject:</Typography>
              <Typography variant="body2">{assignDialog.item.subject}</Typography>
            </Box>
          )}

          {!assignDialog.selectedLead && (
            <>
              <TextField
                size="small"
                autoFocus
                fullWidth
                placeholder="Search leads by name, website, …"
                value={assignDialog.leadQuery}
                onChange={(e) => setAssignDialog((d) => ({ ...d, leadQuery: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                }}
                sx={{ mb: 1.5 }}
              />
              {assignDialog.leadLoading && <Stack direction="row" spacing={1}><CircularProgress size={16} /><Typography variant="caption">Searching…</Typography></Stack>}
              {!assignDialog.leadLoading && assignDialog.leadQuery.trim().length >= 2 && assignDialog.leadResults.length === 0 && (
                <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No leads match.</Typography>
              )}
              <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {assignDialog.leadResults.map((l) => (
                  <Box key={l.id} onClick={() => pickLead(l)}
                    sx={{ p: 1, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: '#eef2ff' }, mb: 0.5, border: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>#{l.id} {l.companyName}</Typography>
                    {l.website && <Typography variant="caption" sx={{ color: '#64748b' }}>{l.website}</Typography>}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {assignDialog.selectedLead && (
            <>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Chip size="small" label={`#${assignDialog.selectedLead.id} ${assignDialog.selectedLead.companyName}`} color="primary" />
                <Button size="small" onClick={() => setAssignDialog((d) => ({ ...d, selectedLead: null, contacts: [], selectedContactId: null, createNew: false }))}>
                  Change lead
                </Button>
              </Stack>

              <Typography variant="subtitle2" sx={{ mb: 0.75, color: '#475569' }}>Contact</Typography>
              {assignDialog.contactsLoading && <CircularProgress size={16} />}
              {!assignDialog.contactsLoading && !assignDialog.createNew && (
                <>
                  <Box sx={{ maxHeight: 220, overflowY: 'auto', mb: 1 }}>
                    {assignDialog.contacts.length === 0 && (
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No contacts on this lead. Create one below.</Typography>
                    )}
                    {assignDialog.contacts.map((c) => (
                      <Box key={c.id}
                        onClick={() => setAssignDialog((d) => ({ ...d, selectedContactId: c.id }))}
                        sx={{
                          p: 1, mb: 0.5, borderRadius: 1, cursor: 'pointer',
                          border: '1px solid',
                          borderColor: assignDialog.selectedContactId === c.id ? '#6366f1' : '#e2e8f0',
                          bgcolor: assignDialog.selectedContactId === c.id ? '#eef2ff' : '#fff',
                        }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {[c.firstName, c.lastName].filter(Boolean).join(' ') || '(no name)'}
                          {c.isPrimary && <Chip size="small" label="primary" sx={{ ml: 1, height: 16, fontSize: '0.6rem' }} />}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>{c.email || '(no email)'}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button size="small" onClick={() => setAssignDialog((d) => ({ ...d, createNew: true, selectedContactId: null }))}>
                    + Create new contact instead
                  </Button>
                </>
              )}
              {assignDialog.createNew && (
                <Stack spacing={1.5}>
                  <TextField size="small" label="Email" value={assignDialog.newEmail}
                    onChange={(e) => setAssignDialog((d) => ({ ...d, newEmail: e.target.value }))} />
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" label="First name" value={assignDialog.newFirst}
                      onChange={(e) => setAssignDialog((d) => ({ ...d, newFirst: e.target.value }))} sx={{ flex: 1 }} />
                    <TextField size="small" label="Last name" value={assignDialog.newLast}
                      onChange={(e) => setAssignDialog((d) => ({ ...d, newLast: e.target.value }))} sx={{ flex: 1 }} />
                  </Stack>
                  <Button size="small" onClick={() => setAssignDialog((d) => ({ ...d, createNew: false }))}>
                    Back to existing contacts
                  </Button>
                </Stack>
              )}
            </>
          )}

          {assignDialog.error && <Alert severity="error" sx={{ mt: 1.5 }}>{assignDialog.error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssign} disabled={assignDialog.saving}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              assignDialog.saving
              || !assignDialog.selectedLead
              || (!assignDialog.createNew && !assignDialog.selectedContactId && assignDialog.contacts.length > 0)
              || (assignDialog.createNew && !assignDialog.newEmail.trim())
            }
            onClick={submitAssign}>
            {assignDialog.saving ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={replyDialog.open} onClose={closeReply} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReplyIcon fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>Reply</Typography>
            {replyDialog.item && (
              <Chip size="small" label={`#${replyDialog.item.leadId} ${replyDialog.item.companyName || ''}`} variant="outlined" sx={{ ml: 1 }} />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              label="To"
              value={replyDialog.to}
              onChange={(e) => setReplyDialog((d) => ({ ...d, to: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Subject"
              value={replyDialog.subject}
              onChange={(e) => setReplyDialog((d) => ({ ...d, subject: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Body (HTML or plain text)"
              value={replyDialog.body}
              onChange={(e) => setReplyDialog((d) => ({ ...d, body: e.target.value }))}
              fullWidth
              multiline
              minRows={10}
            />
            <TextField
              size="small"
              label="AI instructions (optional, applied on Improve)"
              placeholder="e.g. keep it under 4 sentences, friendly tone, address their pricing concern"
              value={replyDialog.customInstructions}
              onChange={(e) => setReplyDialog((d) => ({ ...d, customInstructions: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Improve uses the same AI prompt as the lead activity page (provider: <strong>{aiProvider}</strong>).
            </Typography>
            {replyDialog.error && <Alert severity="error">{replyDialog.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={closeReply} disabled={replyDialog.sending}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={replyDialog.improving ? <CircularProgress size={14} /> : <ImproveIcon fontSize="small" />}
            onClick={handleImproveReply}
            disabled={replyDialog.improving || replyDialog.sending || !replyDialog.body.trim()}
          >
            {replyDialog.improving ? 'Improving…' : 'Improve with AI'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon fontSize="small" />}
            onClick={handleSendReply}
            disabled={replyDialog.sending || replyDialog.improving || !replyDialog.to.trim() || !replyDialog.subject.trim() || !replyDialog.body.trim()}
          >
            {replyDialog.sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
